// ===========================================
// Cloudflare Turnstile widget (#412)
// ===========================================
//
// The browser half of the spam check on the two public forms that mail a typed
// address. The token it produces is verified on the SERVER (siteverify); this
// widget alone stops nothing. Renders nothing when the school info carries no
// site key (keys not configured), so local dev and tests work without Cloudflare.
//
// ⚠️ Import this ONLY from pages/public/EnquiryFormPage.tsx and
// pages/public/MeetAndGreetBookingPage.tsx (turnstileScope.test.ts refuses any
// other importer): never a page whose URL carries a token. Plan md/plan/412.md
// D5, §3.6.

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Box } from '@mui/material';
import { loadTurnstile, type TurnstileApi } from '../../utils/loadTurnstile';

export type TurnstileAction = 'enquiry' | 'meet_and_greet';

export interface TurnstileWidgetHandle {
  /** Tokens are single use: call after every FAILED submit attempt (a success unmounts the form, which removes the widget). */
  reset: () => void;
}

interface TurnstileWidgetProps {
  siteKey: string | null | undefined;
  action: TurnstileAction;
  /** A fresh token, or null when it expired or was reset. */
  onToken: (token: string | null) => void;
  /**
   * The script or the widget cannot run (blocked, offline, an error, an unsupported
   * browser). A challenge the visitor did not finish in time is NOT this: it only
   * clears the token (`onToken(null)`), because the widget still works.
   */
  onUnavailable: () => void;
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget(
  { siteKey, action, onToken, onUnavailable },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const apiRef = useRef<TurnstileApi | null>(null);
  // Keep the latest callbacks without re-rendering the widget when they change.
  const onTokenRef = useRef(onToken);
  const onUnavailableRef = useRef(onUnavailable);
  onTokenRef.current = onToken;
  onUnavailableRef.current = onUnavailable;

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current(null);
      if (apiRef.current && widgetIdRef.current) apiRef.current.reset(widgetIdRef.current);
    },
  }), []);

  const markUnavailable = useCallback(() => onUnavailableRef.current(), []);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadTurnstile()
      .then((api) => {
        if (cancelled || !containerRef.current) return;
        apiRef.current = api;
        widgetIdRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          action,
          appearance: 'interaction-only',
          size: widgetSize(),
          theme: 'light',
          'refresh-expired': 'auto',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          // A challenge the visitor did not finish in time: no token yet, but the
          // widget works (Cloudflare re-presents it), so this is not "unavailable".
          'timeout-callback': () => onTokenRef.current(null),
          'error-callback': markUnavailable,
          'unsupported-callback': markUnavailable,
        });
      })
      .catch(() => {
        if (!cancelled) markUnavailable();
      });

    return () => {
      cancelled = true;
      if (apiRef.current && widgetIdRef.current) apiRef.current.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [siteKey, action, markUnavailable]);

  if (!siteKey) return null;
  return <Box ref={containerRef} data-testid="turnstile-widget" sx={{ mb: 2, maxWidth: '100%' }} />;
});

/**
 * `flexible` has a 300 px minimum, wider than the form on a 320–375 px phone
 * (container + Paper padding leave ~240–295 px), so a visible challenge would
 * scroll the page sideways (/qa Phase 5 L2). `compact` is 150 px wide.
 */
function widgetSize(): 'flexible' | 'compact' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'flexible';
  return window.matchMedia('(max-width: 400px)').matches ? 'compact' : 'flexible';
}

export default TurnstileWidget;
